import { getYmdFromIso } from 'oxygen-lib-ui/helpers/date';
import { getGaPageType, preparePostBundleItemTypes } from 'util/ga';
import { getOriginatingSource } from 'util/originatingSource';
import { getCurrentWindowUrl } from 'util/window';
import { FLIP_PREFIX } from 'util/flips';
import { getPostIdFromUrl } from 'util/link';

// these custom triggers are defined in GTM
// https://tagmanager.google.com/#/container/accounts/3094636345/containers/8965748/
export const PAGE_LOADED_TRIGGER = 'pageLoaded';
export const BUNDLES_LOADED_TRIGGER = 'moreBundlesLoaded';

export const MEMBER = 'Member';
export const ANONYMOUS = 'Anonymous';

// a list of all the GTM vars we use. this should be kept up to date with any
// new vars we set in getGtmVarsForPageLoadedEvent() below. if not, then we
// could end up with some GTM vars whose values persist across page views, which
// is something we don't (currently) desire.
const GTM_VARS = [
  // Current Page Title {String}
  'pageTitle',

  // Current Page URL {String}
  'pageUrl',

  // Canonical URL {String}
  'canonical',

  // Device Pixel Ratio
  'devicePixelRatio',

  // HTTP Status {String}
  'httpStatus',

  // Event Data {Any}
  // useful with custom events/triggers
  'eventData',

  // User State {String}
  'atm.userState',

  // User ID {String}
  'atm.userId',

  // CLEANUP: fix this once the originatingSource lib is refactored
  'atm.originatingSource',

  // Page Type {String}
  'atm.pageType',

  // Post ID {String}
  'atm.postId',

  // Author {String}
  'atm.author',

  // Category {String}
  // NOTE: We capture category and categories to differentiate between the
  // primary and other categories.
  'atm.category',

  // Categories - Array {String[]}
  // has corresponding "Categories - String" var in GTM
  'atm.categories',

  // Tags - Array {String[]}
  // has corresponding "Tags - String" var in GTM
  'atm.tags',

  // Knowledge Tags - Array {String[]}
  // has corresponding "Knowledge Tags - String" var in GTM
  'atm.knowledgeTags',

  // Date Published {String}
  'atm.datePublished',

  // Date Updated {String}
  'atm.dateUpdated',

  // Prism Colors {String}
  'atm.prismColors',

  // Prism Tags {String}
  'atm.prismTags',

  // Bundle Depth {Number}
  'atm.bundleDepth',

  // Feature Flips {String}
  'atm.featureFlips',

  // SSR Render Datetime {String}
  'atm.ssrRenderDatetime',

  // Deploy ID {String}
  'atm.deployId',

  // Surrogate Key {String}
  'atm.surrogateKey',

  // Recipe taxonomy {String}
  'atm.recipeTaxonomy',

  // Teams - Array {String[]}
  // has corresponding "Teams - String" var in GTM
  'atm.teams',

  // Platforms - Array {String[]}
  // has corresponding "Platforms - String" var in GTM
  'atm.platforms',

  // Post Bundle Item Types - Object {Object}
  // has corresponding "Post Bundle Item Types - String" var in GTM
  'atm.postBundleItemTypes'
];

// build up an object that has each of the GTM_VARS set to null. this is used as
// the "base" GTM values, which we override for each new page we view.
// ensure that all values are reset between page views.
export const DEFAULT_GTM_DATA = GTM_VARS.reduce(
  (acc, curr) => ({
    ...acc,
    [curr]: null
  }),
  {}
);

/**
 * extracts relevant data from JSON-LD object and formats it for use in GTM.
 *
 * NOTE: is is important that any missing values returned are represented `null`
 * since that is the only consistent value that overwrites a previous GTM
 * variable value.
 *
 * @export
 * @param {Object} [jsonLd={}]
 * @param {String} [jsonLd.articleSection]
 * @param {Object} [jsonLd.author]
 * @param {String} [jsonLd.author.name]
 * @param {String[]} [jsonLd.keywords]
 * @param {String} [jsonLd.datePublished]
 * @param {String} [jsonLd.dateModified]
 * @param {String} [jsonLd.dateUpdated]
 * @param {String} [jsonLd.dateRepromoted]
 * @returns {Object} metadata object suited for directly assigning to GTM vars
 */
export function extractGtmMetadataFromJsonLd(jsonLd = {}) {
  const {
    articleSection,
    author,
    keywords,
    datePublished,
    dateModified,
    dateUpdated,
    dateRepromoted
  } = jsonLd;

  const categories = (articleSection && articleSection.split(',')) || null;
  const category = (categories && categories[0]) || null;

  // empty arrays don't override existing values in GTM, so we need to be really
  // explicit here.
  const tags = keywords && keywords.length > 0 ? keywords : null;
  const filteredTags = (tags && tags.filter(t => !/^(team):/.test(t))) || null;
  const knowledgeTags =
    (filteredTags &&
      filteredTags.filter(t => /^(knowledge|purpose|platform):/.test(t))) ||
    null;
  const filteredTeamTags =
    (tags && tags.filter(t => /^(team):/.test(t))) || null;
  // teams should be excluded from all tag lists and only appear in our teams GTM var
  const teamTags =
    filteredTeamTags && filteredTeamTags.length > 0 ? filteredTeamTags : null;
  const filteredPlatformTags =
    (filteredTags && filteredTags.filter(t => /^(platform):/.test(t))) || null;
  const platformTags =
    filteredPlatformTags && filteredPlatformTags.length > 0
      ? filteredPlatformTags
      : null;

  return {
    author: (author && author.name) || null,
    category,
    categories,
    tags: filteredTags,
    knowledgeTags,
    teams: teamTags,
    platforms: platformTags,
    datePublished: getYmdFromIso(datePublished) || null,
    dateModified: getYmdFromIso(dateModified) || null,
    dateUpdated: getYmdFromIso(dateUpdated) || null,
    dateRepromoted: getYmdFromIso(dateRepromoted) || null
  };
}

/**
 * returns a data object formatted for passing to the GTM data layer to
 * represent a pageLoaded event.
 *
 * NOTE: it is important that any missing values returned are represented `null`
 * since that is the only consistent value that overwrites a previous GTM
 * variable value.
 *
 * @export
 * @param {Object} data
 * @param {String} data.path
 * @param {Object} data.metadata
 * @param {String} data.metadata.title
 * @param {String} data.metadata.canonical
 * @param {Object} data.metadata.jsonLd
 * @param {String[]} data.metadata.prismColors
 * @param {String[]} data.metadata.prismTags
 * @param {String} data.metadata.recipeTaxonomy
 * @param {String} data.pageType
 * @param {String} data.httpStatus
 * @param {Object} data.user
 * @param {String} data.user.communityId
 * @param {String[]} data.flips
 * @param {String} data.ssrRenderDatetime
 * @param {String} data.deployId
 * @param {String} data.surrogateKey
 * @param {Object} data.contentStats
 * @returns {Object} GTM vars destined for data layer
 */
export function getGtmVarsForPageLoadedEvent(data = {}) {
  const {
    path,
    metadata = {},
    pageType,
    httpStatus = null,
    user,
    flips,
    ssrRenderDatetime,
    deployId,
    surrogateKey,
    contentStats
  } = data;

  const {
    title: pageTitle,
    canonical,
    jsonLd,
    prismColors = null,
    prismTags = null,
    recipeTaxonomy = null
  } = metadata;

  // pull some things from the window/document
  const pageUrl = getCurrentWindowUrl(window.location, path) || null;

  // note that we're not defaulting this to 0 for GTM because GA cannot treat
  // custom dimensions as integers. therefore an empty field is a-ok.
  const postId = getPostIdFromUrl(pageUrl);

  const devicePixelRatio = window.devicePixelRatio || null;
  const originatingSource = getOriginatingSource(document.referrer) || null;

  const {
    author,
    category,
    categories,
    tags,
    knowledgeTags,
    teams,
    platforms,
    datePublished,
    dateUpdated,
    dateModified,
    dateRepromoted
  } = extractGtmMetadataFromJsonLd(jsonLd);

  const gaPageType = getGaPageType(pageType) || null;

  const userState = user ? MEMBER : ANONYMOUS;
  const userId = user && user.atid ? user.atid : null;

  const formattedPrismColors =
    prismColors && prismColors.length > 0 ? prismColors.join(', ') : null;
  const formattedPrismTags =
    prismTags && prismTags.length > 0 ? prismTags.join(', ') : null;

  const formattedFlips =
    flips && flips.length > 0
      ? flips.map(f => f.replace(FLIP_PREFIX, '')).join(', ')
      : null;

  const postBundleItemTypes =
    contentStats && contentStats.post && contentStats.post.bundleItemTypes
      ? preparePostBundleItemTypes(contentStats.post.bundleItemTypes)
      : null;

  return {
    // resets the timer for the page since we're using client-side navigation.
    // we want times tracked from load of each page, not the initial page
    // viewed.
    //
    // NOTE: on initial page render this ends up overwriting the gtm.start
    // sent with the gtm.js event (see GoogleTagManager#componentDidMount)
    // because this event fires when the data resolves a short time later (a
    // few hundred ms?). for right now i think that's fine; the relative
    // timing from client-side navigation is more important.
    'gtm.start': new Date().getTime(),

    // these title and URL vars are used instead of GTM's built-in vars because
    // we need direct control over the title/URL: we can't rely on GTM to pick
    // them up from the <title> or location bar because, during client side
    // routing, our data resolves after the history API event happens.
    pageTitle,
    pageUrl,
    canonical,
    devicePixelRatio,
    httpStatus,
    'atm.userState': userState,
    'atm.userId': userId,

    'atm.originatingSource': originatingSource,

    'atm.pageType': gaPageType,
    'atm.postId': postId,
    'atm.author': author,
    'atm.category': category,
    'atm.categories': categories,
    'atm.tags': tags,
    'atm.knowledgeTags': knowledgeTags,
    'atm.datePublished': datePublished,
    'atm.dateUpdated': dateUpdated,
    'atm.dateRepromoted': dateRepromoted,
    'atm.dateModified': dateModified,
    'atm.prismColors': formattedPrismColors,
    'atm.prismTags': formattedPrismTags,
    'atm.featureFlips': formattedFlips,
    'atm.ssrRenderDatetime': ssrRenderDatetime || null,
    'atm.deployId': deployId,
    'atm.surrogateKey': surrogateKey,
    'atm.recipeTaxonomy': recipeTaxonomy,
    'atm.teams': teams,
    'atm.platforms': platforms,
    'atm.postBundleItemTypes': postBundleItemTypes
  };
}

/**
 * returns a data object formatted for passing to the GTM data layer to
 * represent a bundlesLoaded event.
 *
 * NOTE: is is important that any missing values returned are represented `null`
 * since that is the only consistent value that overwrites a previous GTM
 * variable value.
 *
 * @param {Object} data
 * @param {Number} data.bundleCount
 * @returns {Object} GTM vars destined for data layer
 */
export function getGtmVarsForBundlesLoadedEvent(data = {}) {
  const { bundleCount } = data;

  const bundleDepth = parseInt(bundleCount, 10) || null;

  return {
    'atm.bundleDepth': bundleDepth
  };
}
